import React, { useEffect, useMemo, useRef, useState } from "react";
import { ref, get, set, runTransaction, push, update, increment } from "firebase/database";
import { doc, getDoc } from "firebase/firestore";
import { database, auth, firestore } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import { DEFAULT_COST_PER_KG, TRANSACTION_DIRECTION } from "../../../constants/farmerConstants";
import UnitPricePromptModal from "./UnitPricePromptModal";
import { logRecordAuditEvent } from "../../../utils/recordAuditLog";

/**
 * Helper function to determine transaction direction based on cost difference in milk record edits
 * @param {number} costDifference - The difference in cost (new cost - old cost)
 * @returns {string} - "credit" or "debit"
 */
const getMilkEditTransactionDirection = (costDifference) => {
  return costDifference >= 0
    ? TRANSACTION_DIRECTION.CREDIT  // cost increased, we owe more
    : TRANSACTION_DIRECTION.DEBIT;  // cost decreased, we owe less
};

/**
 * RecordMilkModal
 * - Supports both adding a new milk record and editing an existing one.
 * - Props:
 *   - farmer: { otherName, name, costPerKg }
 *   - companyName: string
 *   - selectedDate: yyyy-mm-dd (optional; falls back to today)
 *   - existingRecord: if present, object containing { id, ...record } to edit
 *   - onClose: fn
 *   - onSaved: fn called after successful save (new or edit)
 */
const RecordMilkModal = ({
  farmer,
  companyName,
  selectedDate: propDate,
  existingRecord,
  onClose,
  onSaved,
  prefillTotalKgs = null,
}) => {
  const [quantity, setQuantity] = useState("");
  const [tare, setTare] = useState("");
  const [total, setTotal] = useState(null);
  const [email, setEmail] = useState("");
  const [collectorName, setCollectorName] = useState("");
  const [loading, setLoading] = useState(false);
  const fetchedNameRef = useRef(false);
  const lastUidRef = useRef(null);

  // Resolved cost per kg: set from prompt when farmer has no saved price
  const [resolvedCostPerKg, setResolvedCostPerKg] = useState(null);
  // Show price prompt for new records when farmer has no saved costPerKg
  const farmerHasPrice = farmer?.costPerKg != null && parseFloat(farmer.costPerKg) > 0;
  const [showPricePrompt, setShowPricePrompt] = useState(!farmerHasPrice && !existingRecord);

  // The effective cost per kg: from prompt override → saved farmer price → default
  const effectiveCostPerKg = useMemo(
    () => resolvedCostPerKg ?? farmer?.costPerKg ?? DEFAULT_COST_PER_KG,
    [resolvedCostPerKg, farmer]
  );

  // Date management
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = propDate ? new Date(propDate) : new Date();
    return today.toISOString().split("T")[0];
  });

  useEffect(() => {
    if (!auth || !firestore) return () => {};

    const unsub = auth.onAuthStateChanged((u) => {
      if (u?.email) setEmail(u.email);

      if (u?.uid && u.uid !== lastUidRef.current) {
        lastUidRef.current = u.uid;
        fetchedNameRef.current = false;
        setCollectorName("");
      }

      if (u?.uid && !fetchedNameRef.current) {
        const currentUid = u.uid;
        fetchedNameRef.current = true;
        getDoc(doc(firestore, "users", u.uid))
          .then((snap) => {
            if (lastUidRef.current !== currentUid) return;
            const fullName = snap.exists() ? snap.data()?.fullName : "";
            if (fullName) setCollectorName(fullName);
          })
          .catch((err) => {
            console.warn("Could not load collector name", err);
            // fallback remains email
          });
      }
    });
    return () => unsub();
  }, []);

  // If editing, prefill fields
  useEffect(() => {
    if (existingRecord) {
      const rec = existingRecord;
      // rec may have TotalKgs stored as string (e.g. "12.3")
      setTotal(parseFloat(rec.TotalKgs || "0") || null);
      // We don't know original quantity/tare - leave blank but allow editing total directly by populating quantity = total and tare=0
      setQuantity(rec.TotalKgs ? String(rec.TotalKgs) : "");
      setTare("0");
      // if existing record has Date string in dd/MM/yyyy, try to parse to yyyy-mm-dd
      if (rec.Date) {
        const parts = rec.Date.split("/");
        if (parts.length === 3) {
          const d = parts[0].padStart(2, "0");
          const m = parts[1].padStart(2, "0");
          const y = parts[2];
          setSelectedDate(`${y}-${m}-${d}`);
        }
      }
    } else {
      setSelectedDate(propDate || new Date().toISOString().split("T")[0]);
    }
  }, [existingRecord, propDate]);

  useEffect(() => {
    if (!existingRecord && prefillTotalKgs) {
      const parsedPrefill = parseFloat(prefillTotalKgs);
      if (!Number.isNaN(parsedPrefill)) {
        setQuantity(String(parsedPrefill));
        setTare("0");
        setTotal(parsedPrefill);
      }
    }
  }, [prefillTotalKgs, existingRecord]);

  // Auto-calc total when quantity/tare change (for new records)
  useEffect(() => {
    const q = parseFloat(quantity) || 0;
    const t = parseFloat(tare) || 0;
    const diff = q - t;
    setTotal(diff > 0 ? Number(diff.toFixed(1)) : null);
  }, [quantity, tare]);

  const getYMD = () => {
    const dateObj = new Date(selectedDate);
    const y = dateObj.getFullYear();
    const m = (dateObj.getMonth() + 1).toString();
    const d = dateObj.getDate().toString();
    return { y, m, d };
  };

  // checkExists only used when creating new record; when editing we allow updating existing key
  const checkExists = async () => {
    const { y, m, d } = getYMD();
    const path = `Companies/${companyName}/FarmerRecords/${farmer.otherName}/${y}/${m}/${d}`;
    const snap = await get(ref(database, path));
    return snap.exists();
  };

  const handleSave = async () => {
    if (!farmer || !companyName) {
      toast.error("Missing farmer or company");
      return;
    }
    const qtyToUse = total || (parseFloat(quantity || "0") - parseFloat(tare || "0"));
    if (!qtyToUse || qtyToUse <= 0) {
      toast.error("Enter valid milk quantity.");
      return;
    }

    setLoading(true);

    try {
      const { y, m, d } = getYMD();
      const dateStr = new Date().toISOString();

      // Calculate the cost based on the effective cost per kg (resolved from prompt or farmer profile)
      const costPerKg = effectiveCostPerKg;
      const milkCost = qtyToUse * costPerKg;

      // if editing, update the existing keys, otherwise create a new one
      if (existingRecord && existingRecord.id) {
        const key = existingRecord.id;

        // Calculate the difference in cost for balance adjustment
        const previousQty = parseFloat(existingRecord.TotalKgs || 0);
        const previousCost = previousQty * costPerKg;
        const costDifference = milkCost - previousCost;

        const milkData = {
          ...existingRecord,
          FarmerId: existingRecord?.FarmerId || (farmer.otherName.replace(/\D+/g, "") || ""),
          ReceiptNo: existingRecord?.ReceiptNo || key,
          UserName: farmer.otherName,
          CloudName: existingRecord?.CloudName || "",
          Status: "",
          CollectedBy: collectorName || email,
          Date: selectedDate.split("-").reverse().join("/"), // dd/MM/yyyy
          TotalKgs: String(qtyToUse),
          CostPerKg: String(costPerKg),
          TotalCost: String(milkCost),
        };

        const companyPath = `Companies/${companyName}/MilkCollected/${y}/${m}/${d}/${key}`;
        const newRecordCompany = `Companies/${companyName}/NewCollectedMilkRecord/${key}`;
        const newRecordRoot = `NewCollectedMilkRecord/${key}`;
        const farmerPath = `Companies/${companyName}/FarmerRecords/${farmer.otherName}/${y}/${m}/${d}`;
        const farmerBalancePath = `Companies/${companyName}/ValidatedFarmers/${farmer.otherName}`;

        // Record transaction in FarmerPayable account
        const payableTxPath = `Companies/${companyName}/Accounts/farmer_payable/${y}/${m}/${key}`;
        const payableTxData = {
          id: key,
          amount: Math.abs(costDifference),
          direction: getMilkEditTransactionDirection(costDifference),
          farmerName: farmer.name,
          farmerOtherName: farmer.otherName,
          description: `Milk record edit for ${farmer.name} - ${qtyToUse} Kgs`,
          date: dateStr,
          collectedBy: collectorName || email,
        };

        await Promise.all([
          set(ref(database, companyPath), milkData),
          set(ref(database, newRecordCompany), milkData),
          set(ref(database, newRecordRoot), milkData),
          set(ref(database, farmerPath), String(qtyToUse)), // keep FarmerRecords date node consistent
          // Update farmer balance with the cost difference
          update(ref(database, farmerBalancePath), {
            Balance: increment(costDifference)
          }),
          // Record transaction in company FarmerPayable account
          set(ref(database, payableTxPath), payableTxData),
        ]);

        await logRecordAuditEvent({
          action: "edit",
          entityType: "milk_collection_record",
          companyName,
          recordId: key,
          sourceComponent: "MilkCollectionTab",
          oldData: existingRecord,
          newData: milkData,
        });

        toast.success("Milk record updated.");
        onSaved && onSaved();
        onClose();
        setLoading(false);
        return;
      }

      // NEW record flow
      const exists = await checkExists();
      if (exists) {
        toast.error("Record already exists for this farmer on this day.");
        setLoading(false);
        return;
      }

      const key = push(ref(database)).key;
      const milkData = {
        FarmerId: farmer.otherName.replace(/\D+/g, "") || "",
        ReceiptNo: key,
        UserName: farmer.otherName,
        CloudName: farmer.cloudName || "",
        Status: "",
        CollectedBy: collectorName || email,
        Date: selectedDate.split("-").reverse().join("/"), // dd/MM/yyyy
        TotalKgs: String(qtyToUse),
        CostPerKg: String(costPerKg),
        TotalCost: String(milkCost),
      };

      // paths (same as your mobile logic)
      const farmerPath = `Companies/${companyName}/FarmerRecords/${farmer.otherName}/${y}/${m}/${d}`;
      const collectedPath = `Companies/${companyName}/MilkCollected/${y}/${m}/${d}/${key}`;
      const newRecordCompany = `Companies/${companyName}/NewCollectedMilkRecord/${key}`;
      const newRecordRoot = `NewCollectedMilkRecord/${key}`;
      const farmerBalancePath = `Companies/${companyName}/ValidatedFarmers/${farmer.otherName}`;

      // Record transaction in FarmerPayable account
      const payableTxPath = `Companies/${companyName}/Accounts/farmer_payable/${y}/${m}/${key}`;
      const payableTxData = {
        id: key,
        amount: milkCost,
        direction: TRANSACTION_DIRECTION.CREDIT, // credit increases payable (what company owes to farmer)
        farmerName: farmer.name,
        farmerOtherName: farmer.otherName,
        farmerCloudName: farmer.cloudName || "",
        description: `Milk purchase from ${farmer.name} - ${qtyToUse} Kgs @ ${costPerKg} KES/Kg`,
        date: dateStr,
        collectedBy: collectorName || email,
      };

      // ensure no duplicates using runTransaction on farmerPath
      const farmerRef = ref(database, farmerPath);
      await runTransaction(farmerRef, (current) => {
        if (current === null) {
          return String(qtyToUse);
        }
        return; // abort the transaction
      });

      await Promise.all([
        set(ref(database, collectedPath), milkData),
        set(ref(database, newRecordCompany), milkData),
        set(ref(database, newRecordRoot), milkData),
        // Update farmer balance with the milk cost
        update(ref(database, farmerBalancePath), {
          Balance: increment(milkCost)
        }),
        // Record transaction in company FarmerPayable account
        set(ref(database, payableTxPath), payableTxData),
      ]);

      toast.success("Milk record saved.");
      onSaved && onSaved();

      // reset for next entry
      setQuantity("");
      setTare("");
      setTotal(null);

      // auto-increment date forward by one day where appropriate
      const next = new Date(selectedDate);
      next.setDate(next.getDate() + 1);
      const today = new Date();
      if (next > today) {
        setSelectedDate(today.toISOString().split("T")[0]);
      } else {
        setSelectedDate(next.toISOString().split("T")[0]);
      }

      onClose();
    } catch (err) {
      console.error("RecordMilkModal save error", err);
      toast.error("Failed to save milk record.");
    } finally {
      setLoading(false);
    }
  };

  // If modal is shown without farmer, show nothing
  if (!farmer) return null;

  // Show price prompt for new records when farmer has no saved cost per kg
  if (showPricePrompt) {
    return (
      <UnitPricePromptModal
        name={farmer.name}
        type="farmer"
        defaultPrice={DEFAULT_COST_PER_KG}
        onConfirm={(confirmedPrice) => {
          setResolvedCostPerKg(confirmedPrice);
          setShowPricePrompt(false);
        }}
        onCancel={onClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-xl font-bold text-green-700 mb-3">
          {existingRecord ? "Edit Milk Record" : "Record Milk"} – {farmer.name}
        </h2>

        {/* Cost per kg info */}
        <div className="mb-4 p-3 bg-green-50 border border-green-100 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>Cost per Kg:</strong> KES {effectiveCostPerKg}
          </p>
        </div>

        {/* Date Picker */}
        <label className="font-medium text-gray-700">Select Date</label>
        <input
          type="date"
          max={new Date().toISOString().split("T")[0]}
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="w-full border p-2 rounded mb-4"
        />

        {/* Quantity */}
        <label className="font-medium text-gray-700">Total Weight (kg)</label>
        <input
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          placeholder={existingRecord ? "Adjust total or use quantity/tare" : "Total milk + can weight"}
          className="w-full border p-2 rounded mb-4"
        />

        {/* Tare Weight */}
        <label className="font-medium text-gray-700">Tare Weight (kg)</label>
        <input
          type="number"
          value={tare}
          onChange={(e) => setTare(e.target.value)}
          placeholder="Empty can weight"
          className="w-full border p-2 rounded mb-4"
        />

        {/* Total */}
        <div className="mb-4 text-lg">
          Total:{" "}
          <span className="font-bold text-green-700">
            {total ? `${total} Kgs` : existingRecord ? `${existingRecord.TotalKgs} Kgs` : "-"}
          </span>
        </div>

        {/* Estimated cost preview */}
        {total ? (
          <div className="mb-4 p-3 bg-green-50 border border-green-100 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>Estimated Cost:</strong> KES{" "}
              {(total * effectiveCostPerKg).toLocaleString()}
            </p>
          </div>
        ) : null}

        <div className="flex justify-end gap-3">
          <button
            className="px-4 py-2 bg-gray-300 rounded"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>

          <button
            className="px-4 py-2 bg-green-600 text-white rounded disabled:bg-green-300"
            disabled={loading}
            onClick={handleSave}
          >
            {loading ? "Saving..." : existingRecord ? "Save changes" : "Save Record"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecordMilkModal;
